// PendingEnrolmentsPage.tsx
/**
 * Facilitator Pending Enrolments — registrations assigned to the current facilitator that are still
 * at Pending, awaiting accept or decline.
 *
 * Deliberately mirrors `Counselling/PendingCases`: a card grid rather than a table, minimal detail
 * per card, and a review modal on click. Accepting moves the registration to Active; declining
 * requires a reason. See `Components/PendingEnrolmentModal.tsx` for the decline caveat.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Badge, Card, Container, Group, Progress, SimpleGrid, Skeleton, Stack, Text, Title,
  ActionIcon, Tooltip, Button,
} from '@mantine/core';
import {
  IconBooks, IconCalendarEvent, IconCheck, IconMapPin, IconRefresh, IconUsers, IconX,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  useFacilitatorPortalService, FacilitatorRegistrationView,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { EmptyState } from '@components/ccms-components/utils/EmptyState';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { REGISTRATION_STATUS } from '@/pages/Grow/registrationStatusIds';
import FacilitatorPortalNotice from '../Components/FacilitatorPortalNotice';
import PendingEnrolmentModal from './Components/PendingEnrolmentModal';

export default function PendingEnrolmentsPage() {
  const navigate = useNavigate();
  const facilitatorPortal = useFacilitatorPortalService();

  const [registrations, setRegistrations] = useState<FacilitatorRegistrationView[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  // Tracks the card whose accept is in flight, so only that card spins.
  const [actioningId, setActioningId] = useState<number | null>(null);

  // Card click opens the review modal; `rejectTarget` opens it straight into the reason form.
  const [selected, setSelected] = useState<FacilitatorRegistrationView | null>(null);
  const [rejectTarget, setRejectTarget] = useState<FacilitatorRegistrationView | null>(null);
  const modalRegistration = rejectTarget ?? selected;

  const closeModal = () => { setSelected(null); setRejectTarget(null); };

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // `myRegistrations()` then filter, rather than `myRegistrationsByStatus(0)`.
      //
      // The by-status route (`GET api/portal/facilitator/my-registrations/by-status/{id}`) errors on
      // this backend, while plain `my-registrations` is the call `EnrolmentsListView` has always used
      // successfully. Filtering the retrieved set is also what the Cases page now does, and it costs
      // nothing here: a facilitator's registration list is small.
      const result = await facilitatorPortal.myRegistrations();
      const all = Array.isArray(result) ? result : [];
      setRegistrations(all.filter((r) => r.statusID === REGISTRATION_STATUS.Pending));
    } catch (e) {
      // Inline rather than a toast: a 403 here means the account holds the facilitator role with no
      // facilitator record behind it, which is a setup problem the user needs to read.
      console.error('Error fetching pending enrolments:', e);
      setLoadError(e);
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  }, [facilitatorPortal]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  // Accepting needs no reason, so it stays a one-click action on the card as well as in the modal.
  const handleAccept = async (registrationId: number) => {
    setActioningId(registrationId);
    try {
      await facilitatorPortal.changeRegistrationStatus(registrationId, REGISTRATION_STATUS.Active);
      toast.success('Enrolment accepted', 'The enrolment is now active.');
      await fetchPending();
    } catch (e) {
      console.error('Error accepting enrolment:', e);
      toast.error('Error', 'Could not accept the enrolment.');
    } finally {
      setActioningId(null);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" align="flex-end" wrap="nowrap" mb="lg">
        <div>
          <Title order={2}>Pending Enrolments</Title>
          <Text c="dimmed" size="sm">
            Enrolments assigned to you that are waiting to be accepted or declined.
          </Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" size="lg" onClick={fetchPending} loading={loading}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {loadError ? (
        <FacilitatorPortalNotice
          error={loadError}
          subject="your pending enrolments"
          onRetry={fetchPending}
        />
      ) : loading && registrations === null ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} withBorder radius="md" padding="lg">
              <Skeleton height={16} width="70%" mb={8} />
              <Skeleton height={12} width="40%" mb="md" />
              <Skeleton height={12} width="90%" mb={6} />
              <Skeleton height={12} width="60%" mb="md" />
              <Skeleton height={32} width="100%" />
            </Card>
          ))}
        </SimpleGrid>
      ) : !registrations || registrations.length === 0 ? (
        <EmptyState
          variant="no-data"
          entityLabel="pending enrolments"
          onAdd={() => navigate('/Grow/Enrolment')}
          addLabel="View My Enrolments"
        />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {registrations.map((r) => {
            const isActioning = actioningId === r.registrationID;
            return (
              <Card
                key={r.registrationID}
                withBorder
                radius="md"
                padding="lg"
                onClick={() => setSelected(r)}
                style={{ cursor: 'pointer' }}
              >
                <Stack gap="xs" style={{ height: '100%' }}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Text fw={600} lineClamp={1}>
                      {r.courseName ?? r.reference ?? `Registration ${r.registrationID}`}
                    </Text>
                    <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.statusColor)}>
                      {r.statusName ?? '—'}
                    </Badge>
                  </Group>

                  <Group gap={6}>
                    <Badge variant="light" color={r.isLead ? 'blue' : 'gray'} size="sm">
                      {r.isLead ? 'Lead' : 'Support'}
                    </Badge>
                    {r.isRemote && <Badge variant="light" color="grape" size="sm">Remote</Badge>}
                  </Group>

                  <Group gap={6} wrap="nowrap">
                    <IconBooks size={14} opacity={0.6} />
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {r.reference ?? `#${r.registrationID}`}
                    </Text>
                  </Group>

                  <Group gap={6} wrap="nowrap">
                    <IconCalendarEvent size={14} opacity={0.6} />
                    <Text size="sm" c="dimmed" lineClamp={1}>{r.periodDisplay ?? '—'}</Text>
                  </Group>

                  <Group gap={6} wrap="nowrap">
                    <IconMapPin size={14} opacity={0.6} />
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {r.locationName || (r.isRemote ? 'Remote' : '—')}
                    </Text>
                  </Group>

                  <Group gap={6} wrap="nowrap">
                    <IconUsers size={14} opacity={0.6} />
                    <Text size="sm" c="dimmed">
                      {r.participantCount ?? 0} participant{r.participantCount === 1 ? '' : 's'}
                    </Text>
                  </Group>

                  {typeof r.progressPercent === 'number' && (
                    <Progress value={r.progressPercent} size="sm" radius="xl" mt={4} />
                  )}

                  {/* Reject routes through the modal so a reason is always captured. */}
                  <Group grow mt="auto" pt="sm">
                    <Button
                      variant="light"
                      color="red"
                      leftSection={<IconX size={16} />}
                      onClick={(e) => { e.stopPropagation(); setRejectTarget(r); }}
                      disabled={isActioning}
                    >
                      Reject
                    </Button>
                    <Button
                      color="green"
                      leftSection={<IconCheck size={16} />}
                      onClick={(e) => { e.stopPropagation(); handleAccept(r.registrationID); }}
                      loading={isActioning}
                      disabled={isActioning}
                    >
                      Accept
                    </Button>
                  </Group>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      )}

      <PendingEnrolmentModal
        registration={modalRegistration}
        opened={modalRegistration !== null}
        startInReject={rejectTarget !== null}
        onClose={closeModal}
        onActioned={fetchPending}
      />
    </Container>
  );
}
